import { Box, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Toolbar, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BusAdministrationApiService from "../../adapters/xhr/BusAdministrationApiService";
import BusClaimApiService from "../../adapters/xhr/BusClaimApiService";
import { MyButton } from "../../components/my-button/MyButton";
import { MyDateTimePicker } from "../../components/my-date-time-picker/MyDateTimePicker";
import { MyNumberField } from "../../components/my-number-field/MyNumberField";
import { MySelect } from "../../components/my-select/MySelect";
import { MyTextField } from "../../components/my-text-field/MyTextField";
import { DisplayClaimType, lngs } from "../../Constant";
import MyContext from "../../contexts/MyContext";
import { formatDate, formatDateTime, formatTime } from "../../Utils";

export const BusOverviewPage = () => {
  const { t, i18n } = useTranslation();
  const columns = [
    { id: "CaseNumber", label: t("case_number"), minWidth: 170, sortable: true },
    { id: "ClaimStatus", label: t("status"), minWidth: 170, sortable: true },
    { id: "Created", label: t("registred_datetime"), minWidth: 180, sortable: true },
    { id: "TransportationNumber", label: t("transportation_number"), minWidth: 170, sortable: true },
    { id: "DepartureDate", label: t("departure_date"), minWidth: 170, sortable: true },
    { id: "ReimbursementSEK", label: t("reimbursement_SEK"), minWidth: 170, sortable: true },
    { id: "ReceiptTotalCost", label: t("receipttotalcost_SEK"), minWidth: 170 },
    { id: "TicketPriceSEK", label: t("ticket_price_SEK"), minWidth: 170 },
    { id: "DelayMinutes", label: t("delay_min"), minWidth: 170 },
    { id: "DepartureStation", label: t("departure_station"), minWidth: 180, sortable: false },
    { id: "ArrivalStation", label: t("arrival_station"), minWidth: 180, sortable: false },
    { id: "Comment", label: t("comment"), minWidth: 170, sortable: true },
    { id: "TicketValidFrom", label: t("ticket_valid_from"), minWidth: 180 },
    { id: "TicketValidTo", label: t("ticket_valid_to"), minWidth: 180 },
    { id: "TicketNumber", label: t("ticket_number"), minWidth: 170, sortable: true },
    { id: "SentToTopDesk", label: t("sent_to_TopDesk"), minWidth: 170, sortable: true },
    { id: "Changed", label: t("trip_include_change"), minWidth: 170, sortable: true },
    { id: "Canceled", label: t("trip_is_cancelled"), minWidth: 170, sortable: true },
    { id: "Provider", label: t("provider"), minWidth: 170, sortable: false },
    { id: "Reason", label: t("reason"), minWidth: 170, sortable: false },
  ];
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [claims, setClaims] = useState([]);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [ticketNumber, setTicketNumber] = useState("");
  const [identityNumber, setIdentityNumber] = useState("");
  const [caseNumber, setCaseNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [totalReimbursementSEK, setTotalReimbursementSEK] = useState();
  const [transportationNumber, setTransportationNumber] = useState("");
  const [claimStatus, setClaimStatus] = useState([]);
  const [claimStatusOptions, setClaimStatusOptions] = useState([]);
  const [sortBy, setSortBy] = useState("Created");
  const [ascending, setAscending] = useState(false);
  const [showMore, setShowMore] = useState([]);
  const myContext = useContext(MyContext);

  const fetchBusStations = useCallback(async () => {
    let response = await BusAdministrationApiService.getBusStations();
    if (response.isSuccess) {
      myContext.changeStops(response.data);
    }
  }, []);

  const fetchTrainStations = useCallback(async () => {
    let response = await BusAdministrationApiService.getTrainStations();
    if (response.isSuccess) {
      myContext.changeTrainStations(response.data);
    }
  }, []);

  const handleSort = async (field, ascending) => {
    if (field === "DepartureStation") field = "DepartureStationId";
    if (field === "ArrivalStation") field = "ArrivalStationId";
    if (field === "Status") field = i18n.language === lngs.en.code ? "ClaimStatus" : "ClaimStatusSwedish";
    setSortBy(field);
    setAscending(ascending);
  };

  const fetchClaimStatus = useCallback(async () => {
    let response = await BusClaimApiService.getClaimStatus();
    if (response.isSuccess) {
      let data = response.data.map((option) => {
        let rObj = {};
        rObj["key"] = option.id;
        rObj["value"] = i18n.language === lngs.en.code ? option.name : option.swedishName;
        return rObj;
      });
      setClaimStatusOptions(data);
    }
  }, []);

  useEffect(() => {
    if (!claimStatusOptions || claimStatusOptions.length === 0) fetchClaimStatus();
    if (!myContext.trainStations || myContext.trainStations.length === 0) fetchTrainStations();
    if (!myContext.stops || myContext.stops.length === 0) fetchBusStations();
  }, [fetchClaimStatus, fetchTrainStations, fetchBusStations]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeClaimStatus = (value) => {
    setClaimStatus(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getTotalReimbursementSEK = async () => {
    if (ticketNumber) {
      let payload = {
        claimStatus: [],
        displayType: DisplayClaimType.Overview,
        ticketNumber: ticketNumber,
      };
      if (startDate) {
        payload.startDate = formatDate(startDate);
      }
      if (endDate) {
        payload.endDate = formatDate(endDate);
      }
      let response = await BusClaimApiService.getTotalReimbursementSEK(payload);
      if (response?.isSuccess) {
        setTotalReimbursementSEK(response.data);
      }
    } else setTotalReimbursementSEK();
  };

  const fetchClaims = async () => {
    let payload = {
      claimStatus: claimStatus.map((item) => item.key),
      pageNumber: page + 1,
      pageSize: rowsPerPage,
      ascending: ascending,
      sortBy: sortBy,
      displayType: DisplayClaimType.Overview,
    };
    if (transportationNumber) {
      payload.transportationNumber = Number(transportationNumber);
    }
    if (ticketNumber) {
      payload.ticketNumber = ticketNumber;
    }
    if (identityNumber) {
      payload.identityNumber = identityNumber;
    }
    if (caseNumber) {
      payload.caseNumber = caseNumber;
    }
    if (firstName) {
      payload.firstName = firstName;
    }
    if (surname) {
      payload.surname = surname;
    }
    if (startDate) {
      payload.startDate = formatDateTime(startDate);
    }
    if (endDate) {
      payload.endDate = formatDateTime(endDate);
    }
    let response = await BusClaimApiService.getAll(payload);
    if (response?.isSuccess) {
      let data = response.data.items.map((option) => {
        let rObj = {};
        rObj["CaseNumber"] = option.caseNumber;
        rObj["ClaimStatus"] = i18n.language === lngs.en.code ? option.claimStatus?.name : option.claimStatus?.swedishName;
        rObj["Reason"] = option.reason
          ?.split(";")
          .map((item) => t(item))
          .join(" ");
        rObj["Created"] = `${formatDate(option.created)} ${formatTime(option.created, true)}`;
        rObj["TransportationNumber"] = option.transportationNumber;
        rObj["DepartureDate"] = `${formatDate(option.departureDate)} ${formatTime(option.departureDate, false)}`;
        rObj["ReimbursementSEK"] = option.reimbursementSEK;
        rObj["ReceiptTotalCost"] = option.receiptTotalCost;
        rObj["TicketPriceSEK"] = option.ticketPriceSEK;
        rObj["DelayMinutes"] = option.delayMinutes;
        rObj["DepartureStation"] = myContext.trainStations.find((item) => item.id === option.departureStationId)?.name ?? myContext.stops.find((item) => item.id === option.departureStationId)?.longName;
        rObj["ArrivalStation"] = myContext.trainStations.find((item) => item.id === option.arrivalStationId)?.name ?? myContext.stops.find((item) => item.id === option.arrivalStationId)?.longName;
        rObj["Comment"] = option.comment;
        rObj["Provider"] = option.provider;
        rObj["TicketValidFrom"] = `${formatDate(option.ticketValidFrom)} ${formatTime(option.ticketValidFrom, true)}`;
        rObj["TicketValidTo"] = `${formatDate(option.ticketValidTo)} ${formatTime(option.ticketValidTo, true)}`;
        rObj["TicketNumber"] = option.ticketNumber;
        rObj["SentToTopDesk"] = `${formatDate(option.sentToTopDesk)} ${formatTime(option.sentToTopDesk, true)}`;
        rObj["Changed"] = `${option.changed ? t("yes") : t("no")}`;
        rObj["Canceled"] = option.hasCancellationsInPreviousStations ? t("yes") : t("no");
        return rObj;
      });
      setClaims(data);
      setTotalPages(response.data.totalCount);
    }
  };

  useEffect(() => {
    if (myContext.trainStations != null && myContext.trainStations.length > 0 && myContext.stops != null && myContext.stops.length > 0) fetchClaims();
  }, [myContext.trainStations, myContext.stops, page, ascending, sortBy, rowsPerPage]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <Box sx={{ display: "flex" }}>
        <MyDateTimePicker size="small" onAccept={(date) => setStartDate(date.toString())} value={startDate} label={t("from")} style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} />
        <MyDateTimePicker size="small" onAccept={(date) => setEndDate(date.toString())} value={endDate} label={t("to")} style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} />
        <MySelect size="small" style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} label={t("status")} onChange={handleChangeClaimStatus} options={claimStatusOptions} value={claimStatus} />
        <MyNumberField size="small" label={t("transportation_number")} onChange={(e) => setTransportationNumber(e.target.value)} value={transportationNumber} style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} />
        <MyTextField size="small" value={ticketNumber} label={t("ticket_number")} onChange={setTicketNumber} style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} />
        <MyTextField size="small" value={caseNumber} label={t("case_number")} onChange={setCaseNumber} style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} />
        <MyTextField size="small" value={identityNumber} label={t("personal_identifications_number")} onChange={setIdentityNumber} style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} />
        <MyTextField size="small" value={firstName} label={t("firstname")} onChange={setFirstName} style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} />
        <MyTextField size="small" value={surname} label={t("surname")} onChange={setSurname} style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} />
        <MyButton
          text={t("search")}
          color="primary"
          onClick={(e) => {
            setPage(0);
            fetchClaims();
            getTotalReimbursementSEK();
          }}
          style={{ width: "7rem", margin: "1rem 5rem 1rem 1rem" }}
        />
      </Box>
      <Divider />
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h6" id="tableTitle" component="div">
          {`${t("claims")} (${totalPages})`}
          {totalReimbursementSEK !== undefined && ` ${t("total_reimbursement_for_ticket", { amount: totalReimbursementSEK })}`}
        </Typography>
      </Toolbar>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                  {column.sortable ? (
                    <TableSortLabel active={column.id === sortBy} direction={ascending ? "asc" : "desc"} onClick={(e) => handleSort(column.id, !ascending)}>
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {claims.map((row, key) => {
              return (
                <TableRow hover tabIndex={-1} key={key}>
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format && typeof value === "number" ? column.format(value) : showMore.some((item) => item == `${key}`) ? value : value?.toString().substring(0, 40)}
                        {value?.toString().length > 40 && (showMore == [] || !showMore.some((item) => item == key)) && (
                          <i className="selector" onClick={() => setShowMore([...showMore, `${key}`])}>
                            {` ...`}
                          </i>
                        )}
                        {value?.toString().length > 40 && showMore != [] && showMore.some((item) => item == key) && (
                          <strong color="primary.main" className="selector" onClick={() => setShowMore([...showMore].filter((item) => item != key))}>
                            {` ${t("show_less")}`}
                          </strong>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination rowsPerPageOptions={[10, 25, 100]} component="div" count={totalPages} labelRowsPerPage={t("rows_per_page")} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
    </Paper>
  );
};
